//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import {
  SAMPLING_SHIPPING_WEIGHT_ORDER,
  SamplingFulfillmentOption,
  SamplingShippingWeight,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { CaptionSecondaryText, CaptionText } from '@components/atoms/text/Text';
import { countries } from '@constants/countries';
import { capitalize } from '@utils/stringUtils';

interface PrepaidPurchaseOptionsTooltipContentProps {
  fulfillmentOptions: SamplingFulfillmentOption[];
}

const getWeightRange = (weights?: SamplingShippingWeight[]): string => {
  if (!weights || weights.length === 0) {
    return 'N/A';
  }

  const sortedWeights = [...weights].sort((a, b) => {
    return (
      SAMPLING_SHIPPING_WEIGHT_ORDER.indexOf(a) -
      SAMPLING_SHIPPING_WEIGHT_ORDER.indexOf(b)
    );
  });

  if (sortedWeights.length === 1) {
    return sortedWeights[0] ?? 'N/A';
  }

  const first = sortedWeights[0] ?? '';
  const last = sortedWeights[sortedWeights.length - 1] ?? '';

  const getStartWeight = (weight: string) => {
    if (weight.includes('-')) {
      const parts = weight.split('-');
      const start = parts[0];
      const endWithUnit = parts[1];

      if (start && endWithUnit) {
        // Extract unit (e.g. "kg", "lb", "oz") from the end part
        const unit = endWithUnit.replace(/[^a-zA-Z]/g, '').trim();
        return `${start.trim()} ${unit}`;
      }
      return weight;
    }
    return weight;
  };

  const getEndWeight = (weight: string) => {
    if (weight.includes('-')) {
      const parts = weight.split('-');
      return parts[1]?.trim() ?? weight;
    }
    return weight;
  };

  return `${getStartWeight(first)} to ${getEndWeight(last)}`;
};

const getCountryName = (code: string) => {
  const entry = Object.entries(countries).find(([, c]) => c === code);
  return entry ? capitalize(entry[0]) : code;
};

const formatInsertLabel = (insert?: string) => {
  if (!insert) {
    return insert;
  }
  return insert
    .split('_')
    .map((word) => capitalize(word.toLowerCase()))
    .join(' ');
};

export const PrepaidPurchaseOptionsTooltipContent = ({
  fulfillmentOptions,
}: PrepaidPurchaseOptionsTooltipContentProps) => {
  return (
    <Container>
      <CaptionSecondaryText>
        Packages reflects the number purchased and does not account for any used
        in other campaigns
      </CaptionSecondaryText>
      <Table>
        <thead>
          <TableRow>
            <TableHeader>
              <CaptionSecondaryText>Packages</CaptionSecondaryText>
            </TableHeader>
            <TableHeader>
              <CaptionSecondaryText>Type</CaptionSecondaryText>
            </TableHeader>
            <TableHeader>
              <CaptionSecondaryText>Inserts</CaptionSecondaryText>
            </TableHeader>
            <TableHeader>
              <CaptionSecondaryText>Location</CaptionSecondaryText>
            </TableHeader>
            <TableHeader>
              <CaptionSecondaryText>Weight</CaptionSecondaryText>
            </TableHeader>
          </TableRow>
        </thead>
        <tbody>
          {fulfillmentOptions.map((option, index) => (
            <TableRow key={option.id ?? index}>
              <TableCell>
                <CaptionText>{option.cartonCount}</CaptionText>
              </TableCell>
              <TableCell>
                <CaptionText>{option.cartonType}</CaptionText>
              </TableCell>
              <TableCell>
                <CaptionText>{formatInsertLabel(option.insert)}</CaptionText>
              </TableCell>
              <TableCell>
                <CaptionText>
                  {option.availableCountryCodes?.map(getCountryName).join(', ')}
                </CaptionText>
              </TableCell>
              <TableCell>
                <CaptionText>
                  {getWeightRange(option.availableShippingWeights)}
                </CaptionText>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[2]};
  padding: ${({ theme }) => theme.spacings[1]};
  max-width: 600px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableRow = styled.tr``;

const TableHeader = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacings[1]}
    ${({ theme }) => theme.spacings[2]};

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacings[1]}
    ${({ theme }) => theme.spacings[2]};

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }
`;
